import React from 'react';

class LegendErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null, 
            errorInfo: null 
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Enhanced error tracking with component-level identifiers
        const { legendId, legendDescription } = this.props;
        const timestamp = new Date().toISOString();
        const userAgent = navigator.userAgent;
        
        console.error(`🚨🚨🚨 LEGEND ERROR BOUNDARY CAUGHT ERROR 🚨🚨🚨`);
        console.error(`❌ FAILED LEGEND: ${legendId} - ${legendDescription}`);
        console.error(`❌ TIMESTAMP: ${timestamp}`);
        console.error(`❌ USER AGENT: ${userAgent}`);
        console.error(`❌ ERROR MESSAGE:`, error.message);
        console.error(`❌ ERROR NAME:`, error.name);
        console.error(`❌ ERROR STACK:`, error.stack);
        console.error(`❌ COMPONENT STACK:`, errorInfo.componentStack);
        console.error(`❌ ERROR OBJECT:`, error);
        console.error(`❌ ERROR INFO:`, errorInfo);
        
        // Enhanced stack trace parsing to pinpoint exact location
        const stackLines = error.stack ? error.stack.split('\n') : [];
        console.error(`📍 STACK TRACE ANALYSIS:`);
        stackLines.forEach((line, index) => {
            console.error(`📍 Stack Line ${index + 1}: ${line.trim()}`);
            if (line.includes('utils.js') || line.includes('Legend') || line.includes('Chart')) {
                console.error(`🎯 SUSPICIOUS LINE: ${line.trim()}`);
            }
        });
        
        // Component trace analysis  
        const componentLines = errorInfo.componentStack ? errorInfo.componentStack.split('\n') : [];
        console.error(`🧩 COMPONENT TRACE ANALYSIS:`);
        componentLines.forEach((line, index) => {
            console.error(`🧩 Component Line ${index + 1}: ${line.trim()}`);
        });
        
        // Check if this is the Chart.js Legend error
        if (error.message && error.message.includes("Cannot read properties of undefined (reading 'includes')")) {
            console.error(`🎯🎯🎯 CHART.JS LEGEND ERROR CONFIRMED 🎯🎯🎯`);
            console.error(`🎯 THIS IS THE PROBLEMATIC LEGEND: ${legendId}`);
            console.error(`🎯 LEGEND DESCRIPTION: ${legendDescription}`);
            console.error(`🎯 ERROR ORIGINATED FROM: ${legendDescription} in ExpandedMeetingCard`);
            const fileName = legendId.includes('card') ? 'NewAnalysisElements Component' : 'ExpandedMeetingCard.js';
            console.error(`🎯 FILE LOCATION: ${fileName}`);
            console.error(`🎯 COMPONENT PATH: ExpandedMeetingCard -> ${legendDescription} -> Tremor Legend`);
            
            // Enhanced props analysis
            console.error(`🎯 PROPS AT TIME OF ERROR:`, this.props);
            console.error(`🎯 LEGEND ID: ${legendId}`);
            console.error(`🎯 CHILDREN PROPS:`, this.props.children?.props || 'No children props');
            
            // Try to extract Legend data from children props
            if (this.props.children?.props) {
                const legendProps = this.props.children.props;
                console.error(`🎯 LEGEND CATEGORIES:`, legendProps.categories || 'No categories');
                console.error(`🎯 LEGEND COLORS:`, legendProps.colors || 'No colors');
                console.error(`🎯 LEGEND CLASSNAME:`, legendProps.className || 'No className');
            }
            
            // Alert the developer with detailed information
            if (process.env.NODE_ENV === 'development') {
                setTimeout(() => {
                    alert(`🚨 CHART.JS LEGEND ERROR IDENTIFIED! 🚨\n\nProblematic Legend: ${legendId}\nDescription: ${legendDescription}\nFile: ExpandedMeetingCard.js\nComponent Path: ExpandedMeetingCard -> ${legendDescription} -> Tremor Legend\n\nCheck console for detailed stack trace analysis.`);
                }, 100);
            }
            
            // Send enhanced error tracking to console
            console.error(`📊 ERROR TRACKING SUMMARY:`);
            console.error(`📊 Error Type: Chart.js Legend 'includes' TypeError`);
            console.error(`📊 Component ID: ${legendId}`);
            console.error(`📊 Component Description: ${legendDescription}`);
            console.error(`📊 File: ExpandedMeetingCard.js`);
            console.error(`📊 Timestamp: ${timestamp}`);
        }
        
        // Store enhanced error info for display
        this.setState({
            error: error,
            errorInfo: errorInfo,
            timestamp: timestamp,
            userAgent: userAgent
        });
    }

    render() {
        const { legendId, legendDescription, children } = this.props;
        
        if (this.state.hasError) {
            // Enhanced fallback UI with detailed error information
            return (
                <div 
                    style={{
                        border: '3px solid red',
                        padding: '15px',
                        margin: '10px',
                        backgroundColor: '#fee',
                        borderRadius: '8px',
                        fontFamily: 'monospace'
                    }}
                >
                    <h3 style={{ color: 'red', margin: '0 0 15px 0', fontSize: '16px' }}>
                        🚨 LEGEND ERROR BOUNDARY CAUGHT ERROR 🚨
                    </h3>
                    <div style={{ marginBottom: '10px' }}>
                        <p style={{ margin: '5px 0' }}><strong>🎯 Failed Legend ID:</strong> {legendId}</p>
                        <p style={{ margin: '5px 0' }}><strong>📝 Description:</strong> {legendDescription}</p>
                        <p style={{ margin: '5px 0' }}><strong>📄 File:</strong> {legendId.includes('card') ? 'NewAnalysisElements Component' : 'ExpandedMeetingCard.js'}</p>
                        <p style={{ margin: '5px 0' }}><strong>⏰ Timestamp:</strong> {this.state.timestamp || 'N/A'}</p>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                        <p style={{ margin: '5px 0' }}><strong>❌ Error:</strong> {this.state.error?.name || 'Unknown'}</p>
                        <p style={{ margin: '5px 0', color: 'darkred' }}><strong>💬 Message:</strong> {this.state.error?.message || 'No message'}</p>
                    </div>
                    
                    <details style={{ marginTop: '15px' }}>
                        <summary style={{ cursor: 'pointer', padding: '5px', backgroundColor: '#f0f0f0', border: '1px solid #ccc' }}>
                            📍 Full Stack Trace (click to expand)
                        </summary>
                        <pre style={{ 
                            backgroundColor: '#f8f8f8', 
                            padding: '10px', 
                            fontSize: '11px',
                            overflow: 'auto',
                            maxHeight: '200px',
                            border: '1px solid #ddd',
                            marginTop: '5px'
                        }}>
                            {this.state.error?.stack || 'No stack trace available'}
                        </pre>
                    </details>

                    <details style={{ marginTop: '10px' }}>
                        <summary style={{ cursor: 'pointer', padding: '5px', backgroundColor: '#f0f0f0', border: '1px solid #ccc' }}>
                            🧩 Component Stack (click to expand)
                        </summary>
                        <pre style={{ 
                            backgroundColor: '#f8f8f8', 
                            padding: '10px', 
                            fontSize: '11px',
                            overflow: 'auto',
                            maxHeight: '150px',
                            border: '1px solid #ddd',
                            marginTop: '5px'
                        }}>
                            {this.state.errorInfo?.componentStack || 'No component stack available'}
                        </pre>
                    </details>

                    <div style={{ 
                        marginTop: '15px', 
                        padding: '10px', 
                        backgroundColor: '#fff3cd', 
                        border: '1px solid #ffeaa7',
                        borderRadius: '4px'
                    }}>
                        <p style={{ margin: '0', fontSize: '12px', color: '#856404' }}>
                            <strong>🔧 This error boundary has isolated the crash to prevent the entire page from going blank.</strong><br/>
                            Check the browser console for detailed debugging information including stack trace analysis.
                        </p>
                    </div>
                </div>
            );
        }

        return children;
    }
}

export default LegendErrorBoundary;